import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { useAuth } from "@/hooks/useAuth";
import { adminPoliciesAPI, policiesAPI, type PolicyDraftInput } from "@/lib/api/policies";

export const MY_POLICIES_KEY = ["policies", "mine"] as const;
export const POLICY_GATE_KEY = ["policies", "gate"] as const;
export const ADMIN_POLICIES_KEY = ["admin", "policies"] as const;
export const ADMIN_POLICY_OVERVIEW_KEY = ["admin", "policies", "overview"] as const;

/** Published policies with the signed-in user's status (BE #864). */
export const useMyPolicies = () =>
  useQuery({ queryKey: MY_POLICIES_KEY, queryFn: policiesAPI.listMine, staleTime: 0 });

/** How often an open session re-checks whether a new policy was published. */
export const POLICY_RECHECK_MS = 5 * 60 * 1000;

/**
 * Live gate check for a signed-in consultant. `has_pending_policies` on the
 * user is only refreshed at login and session restore, so a policy published
 * mid-session would otherwise go unnoticed; this polls `/policies` (and
 * re-checks on window focus) so the guard catches it. Disabled for admins,
 * who are never gated.
 */
export const usePolicyGateCheck = (enabled: boolean) =>
  useQuery({
    queryKey: POLICY_GATE_KEY,
    queryFn: policiesAPI.checkGate,
    enabled,
    staleTime: 0,
    refetchInterval: POLICY_RECHECK_MS,
    refetchOnWindowFocus: true,
    select: data => data.gate_required,
  });

/**
 * Acknowledge the current version of a policy. When the response says the
 * gate no longer applies, the user is refreshed so `has_pending_policies`
 * drops and the route guard lets the consultant into the portal.
 */
export const useAcknowledgePolicy = () => {
  const queryClient = useQueryClient();
  const { refreshUser } = useAuth();
  return useMutation({
    mutationFn: (policyId: number) => policiesAPI.acknowledge(policyId),
    onSuccess: async result => {
      // Drop the guard's cached re-check rather than just invalidating it: the
      // guard is unmounted on the gate page, and a stale cached "gated" would
      // bounce the consultant straight back here on the way out.
      queryClient.removeQueries({ queryKey: POLICY_GATE_KEY });
      await queryClient.invalidateQueries({ queryKey: MY_POLICIES_KEY });
      if (!result.gate_required) {
        await refreshUser();
      }
    },
  });
};

export const useAdminPolicies = () =>
  useQuery({ queryKey: ADMIN_POLICIES_KEY, queryFn: adminPoliciesAPI.list });

export const usePolicyOverview = () =>
  useQuery({ queryKey: ADMIN_POLICY_OVERVIEW_KEY, queryFn: adminPoliciesAPI.overview });

/** Upload / publish / delete / remind, each refreshing the admin views. */
export const useAdminPolicyActions = () => {
  const queryClient = useQueryClient();
  // The overview key is nested under the list key, so this refreshes both.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ADMIN_POLICIES_KEY });

  const upload = useMutation({
    mutationFn: (input: PolicyDraftInput) => adminPoliciesAPI.uploadDraft(input),
    onSuccess: async () => {
      toast.success("Draft uploaded");
      await invalidate();
    },
  });
  const publish = useMutation({
    mutationFn: (policyId: number) => adminPoliciesAPI.publish(policyId),
    onSuccess: async () => {
      toast.success("Policy published");
      await invalidate();
    },
  });
  const remove = useMutation({
    mutationFn: (policyId: number) => adminPoliciesAPI.deleteDraft(policyId),
    onSuccess: async () => {
      toast.success("Draft deleted");
      await invalidate();
    },
  });
  const remind = useMutation({
    mutationFn: (userIds?: number[]) => adminPoliciesAPI.remind(userIds),
    onSuccess: async result => {
      // Reminders change no statuses, but refresh the matrix so the admin
      // sees anyone who acknowledged in the meantime.
      await queryClient.invalidateQueries({ queryKey: ADMIN_POLICY_OVERVIEW_KEY });
      toast.success(
        result.reminded === 1
          ? "Reminder sent to 1 consultant"
          : `Reminders sent to ${result.reminded} consultants`
      );
    },
  });
  return { upload, publish, remove, remind };
};
